import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CloudService, SnsTopic, ServiceCardData } from '../../core/services/cloud.service';
import { ToastService } from '../../core/services/toast.service';
import { MonitoringService } from '../../core/services/monitoring.service';
import {
  CloudFilters,
  FacetDef,
  SortOption,
  parseFiltersFromParams,
  filtersToParams,
} from './cloud-filter.model';
import { MetricColumn } from './cloud-metrics-panel.component';
import { ActivityItem, findingsToActivity, sortActivity } from './cloud-activity.model';

type Tab = 'overview' | 'topics' | 'activity';

const HISTORY_METRICS: MetricColumn[] = [
  { key: 'published', label: 'Published' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'failed',    label: 'Failed'    },
];

const DEFAULT_SORT = 'name-asc';
const FACET_KEYS   = ['subscriptions', 'monitoring'];

const SORT_OPTIONS: SortOption[] = [
  { value: 'name-asc',           label: 'Name A → Z'         },
  { value: 'name-desc',          label: 'Name Z → A'         },
  { value: 'subscriptions-desc', label: 'Most subscriptions' },
  { value: 'monitored-first',    label: 'Monitored first'    },
];

const FACETS: FacetDef[] = [
  {
    key: 'subscriptions',
    label: 'Subscriptions',
    options: [
      { value: 'subscribed', label: 'Has subscribers' },
      { value: 'none',       label: 'No subscribers'  },
    ],
  },
  {
    key: 'monitoring',
    label: 'Monitoring',
    options: [
      { value: 'monitored',   label: 'Monitored'     },
      { value: 'unmonitored', label: 'Not monitored' },
    ],
  },
];

/**
 * SNS monitoring is at the topic level (CloudWatch TopicName dimension) —
 * failed notification deliveries. The monitored resource identifier is the
 * topic name. Per-subscription delivery breakdown is not available in this
 * version.
 */
@Component({
  selector: 'app-sns-detail',
  templateUrl: './sns-detail.component.html',
})
export class SnsDetailComponent implements OnInit, OnDestroy {
  selectedTab: Tab = 'overview';
  topics: SnsTopic[] = [];
  loading = true;

  filters: CloudFilters = { query: '', facets: {}, sort: DEFAULT_SORT };

  readonly tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'topics',   label: 'Topics'   },
    { id: 'activity', label: 'Activity' },
  ];

  readonly facets      = FACETS;
  readonly sortOptions = SORT_OPTIONS;

  readonly serviceCard: ServiceCardData = { id: 'sns', name: 'SNS', icon: 'megaphone', route: '/cloud/sns', status: 'healthy', metrics: [] };
  readonly historyMetrics = HISTORY_METRICS;

  drawerItemId: string | null = null;
  selectedId: string | null = null;

  activity: ActivityItem[] = [];
  activityLoading = true;
  refreshing = false;

  private sub?: Subscription;
  private routeSub?: Subscription;
  private findingsSub?: Subscription;

  constructor(
    private readonly cloud: CloudService,
    private readonly toast: ToastService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly monitoring: MonitoringService,
  ) {}

  ngOnInit(): void {
    this.cloud.loadItemPoliciesForService('sns').subscribe();
    this.routeSub = this.route.queryParams.subscribe(params => {
      const p = params as Record<string, string>;
      this.filters = parseFiltersFromParams(p, DEFAULT_SORT, FACET_KEYS);
      this.selectedId = p['r'] ?? null;
    });
    this.loadData(false);
  }

  /** Loads the topic list (cache-aware), monitoring state (monitored count), and activity findings. */
  private loadData(bypass: boolean): void {
    this.sub?.unsubscribe();
    this.sub = this.cloud.getSnsTopics(bypass).subscribe(topics => {
      this.topics     = topics;
      this.loading    = false;
      this.refreshing = false;
    });
    this.cloud.loadMonitoringState().subscribe();
    this.findingsSub?.unsubscribe();
    this.findingsSub = this.monitoring.getFindings().subscribe(findings => {
      this.activityLoading = false;
      this.activity        = sortActivity(findingsToActivity(findings, 'SNS'));
    });
  }

  /** Manual refresh — bypass the cache and reload (guarded against double-clicks). */
  refresh(): void {
    if (this.refreshing) return;
    this.refreshing = true;
    this.loadData(true);
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.routeSub?.unsubscribe();
    this.findingsSub?.unsubscribe();
  }

  onActivitySelect(item: ActivityItem): void {
    this.selectedTab = 'topics';
    this.selectResource(item.resourceId);
  }

  // ── Overview counts (standardized: total + monitored, then service-specific) ──

  get totalCount():        number { return this.topics.length; }
  get monitoredCount():    number { return this.topics.filter(t => this.isMonitored(t.name)).length; }
  get subscribedCount():   number { return this.topics.filter(t => t.subscriptionsConfirmed > 0).length; }
  get unsubscribedCount(): number { return this.topics.filter(t => t.subscriptionsConfirmed === 0).length; }

  // ── Filtered + sorted list ──────────────────────────────────────────────────

  get filteredTopics(): SnsTopic[] {
    let result = this.topics;

    const q = this.filters.query.toLowerCase().trim();
    if (q) result = result.filter(item => item.name.toLowerCase().includes(q));

    const subscriptions = this.filters.facets['subscriptions'] ?? [];
    if (subscriptions.length) {
      result = result.filter(item =>
        (subscriptions.includes('subscribed') && item.subscriptionsConfirmed > 0) ||
        (subscriptions.includes('none')       && item.subscriptionsConfirmed === 0));
    }

    const monitoring = this.filters.facets['monitoring'] ?? [];
    if (monitoring.length) {
      result = result.filter(item => {
        const mon = this.isMonitored(item.name);
        return (monitoring.includes('monitored') && mon) || (monitoring.includes('unmonitored') && !mon);
      });
    }

    return this.sorted(result, this.filters.sort);
  }

  private sorted(topics: SnsTopic[], sort: string): SnsTopic[] {
    return [...topics].sort((a, b) => {
      switch (sort) {
        case 'name-asc':           return a.name.localeCompare(b.name);
        case 'name-desc':          return b.name.localeCompare(a.name);
        case 'subscriptions-desc': return b.subscriptionsConfirmed - a.subscriptionsConfirmed || a.name.localeCompare(b.name);
        case 'monitored-first': {
          const aM = this.isMonitored(a.name) ? 0 : 1;
          const bM = this.isMonitored(b.name) ? 0 : 1;
          return aM - bM || a.name.localeCompare(b.name);
        }
        default: return a.name.localeCompare(b.name); // name-asc
      }
    });
  }

  onFiltersChange(f: CloudFilters): void {
    this.router.navigate([], {
      relativeTo:          this.route,
      queryParams:         filtersToParams(f, FACET_KEYS),
      queryParamsHandling: 'merge',
      replaceUrl:          true,
    });
  }

  // ── Monitoring ──────────────────────────────────────────────────────────────

  get quotaUsage() { return this.cloud.getQuotaUsage(); }
  get canMonitorMore(): boolean { return this.cloud.canEnableMoreMonitoring(); }

  isMonitored(topicName: string): boolean { return this.cloud.isMonitored('sns', topicName); }

  toggleMonitoring(topicName: string): void {
    const current = this.cloud.isMonitored('sns', topicName);
    if (!current && !this.cloud.canEnableMoreMonitoring()) {
      this.toast.show(this.cloud.getQuotaExceededMessage(), 'error');
      return;
    }
    this.cloud.toggleMonitoring('sns', topicName, !current).subscribe({
      error: err => {
        if (err.status === 403) {
          const msg = err.error?.code === 'QUOTA_EXCEEDED'
            ? this.cloud.getQuotaExceededMessage()
            : 'Only org owners and admins can configure monitoring.';
          this.toast.show(msg, 'error');
        }
      },
    });
  }

  // ── Metric-history drill-down ───────────────────────────────────────────────

  selectResource(topicName: string): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { r: topicName }, queryParamsHandling: 'merge', replaceUrl: true });
  }
  closeResource(): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { r: null }, queryParamsHandling: 'merge', replaceUrl: true });
  }

  // ── Item settings drawer ────────────────────────────────────────────────────

  openItemSettings(topicName: string): void  { this.drawerItemId = topicName; }
  closeItemSettings(): void                   { this.drawerItemId = null; }

  itemOverrideSummary(topicName: string): string {
    return this.cloud.getItemOverrideSummary('sns', topicName);
  }
}
